import React from 'react';

import './css/Technology.css'

import { Page } from './Page';
import { useBarReveal } from './barReveal';
import { CompareSlider } from './StateOfTheArt';
import { HowItWorks } from './HowItWorks';
import { useCursorRimLight } from './rimLight';

import {
	AI_REMOVAL_EXACT,
	AI_REMOVAL_OTHERS,
	ATTACK_COUNT,
	ATTACK_FAMILIES,
	Attack,
	AttackFamily,
	BEST_ALTERNATIVE_AVERAGE,
	EXACT_RECOVERY_AVERAGE,
	IMAGE_COUNT,
	METHODS,
	Method,
	PAYLOAD_BITS,
	PSNR_DB,
	ROBUSTNESS_RUN,
	SSIM_PERCENT,
} from './robustness'


// A bar's length as a share of the full column, handed to the stylesheet,
// which scales every bar by the same factor so the value printed at the end
// of the longest one still has somewhere to sit, and by a smaller one on a
// phone. The proportions between bars are untouched.
function barShare( percent: number ): number {
	return percent / 100
}

function barStyle( share: number ): React.CSSProperties {
	return { '--share': share } as React.CSSProperties
}

///@brief   The rows the comparison draws one by one: every method, not the
///         header.
const ROW_SELECTOR: string = '[data-bar-row]'

///@brief   The time a full-length bar takes; every bar moves at that pace
///         (barReveal.tsx).
const METHODS_STYLE: React.CSSProperties = { '--speed': '1.4s' } as React.CSSProperties

function iconStyle( icon: string ): React.CSSProperties {
	const url: string = `url(${ icon })`
	return { WebkitMaskImage: url, maskImage: url }
}

function FamilyCard( { family, index }: { family: AttackFamily, index: number } ): React.JSX.Element {

	return (
		<div className="Frame Technology-Family" data-fade data-fade-delay={ ( 0.1 + index * 0.08 ).toFixed( 2 ) } >
			<span className="Technology-Family-Icon" style={ iconStyle( family.icon ) } />
			<div className="Technology-Family-Name" >{ family.name }</div>
			<div className="Technology-Family-Text fs-16" >{ family.description }</div>
			<div className="Technology-Family-Attacks" >
				{ family.attacks.map( ( attack: Attack ): React.JSX.Element => (
					<div className="Technology-Attack" key={ attack.name } >
						<div className="Technology-Attack-Name fs-16" >
							{ attack.name }
							<span className="Technology-Attack-Setting" >{ attack.setting }</span>
						</div>
						<div className="Technology-Attack-Value fs-16" >{ attack.exact }%</div>
					</div>
				) ) }
			</div>
		</div>
	)
}

function MethodRow( { method }: { method: Method } ): React.JSX.Element {

	// --share on the row: the bar's length, and when its figures appear.
	return (
		<div className={ `Technology-Method${ method.bUs ? ' Technology-Method-Us' : '' }` } style={ barStyle( barShare( method.average ) ) } data-bar-row >
			<div className="Technology-Method-Name fs-16" >
				{ method.name }
				{ method.origin && <span className="Technology-Method-Origin" >{ method.origin }</span> }
			</div>
			<div className="Technology-Method-Bar" >
				<div className="Technology-Method-Fill" />
				<div className="Technology-Method-Value fs-16" >{ method.average.toFixed( 1 ) }%</div>
			</div>
			<div className="Technology-Method-Psnr fs-16" >{ method.psnrDb.toFixed( 1 ) } dB</div>
		</div>
	)
}

export function Technology(): React.JSX.Element {

	// The frames of this page catch the same rim light as the home page's.
	const bodyRef = React.useRef<HTMLDivElement>( null )
	useCursorRimLight( bodyRef, '.Frame' )

	// The comparison draws itself row by row as they reach the screen.
	const chartRef = useBarReveal( ROW_SELECTOR )

	return (
		<Page title="Underlayer — Technology" className="Technology" >
		<div ref={ bodyRef } >

			<div className="Pane-Light Page-Section Page-Section-First Technology-Section" >
				<div className="Container" >
					<div className="Page-Label" >Technology</div>
					<div className="Title text-shadow-2" data-fade >Neural watermarking, built to survive the internet.</div>
					<div className="Technology-Intro Page-Text fs-18" data-fade data-fade-delay="0.15" >
						Underlayer hides a { PAYLOAD_BITS }-bit identifier in the pixels of every image it
						serves. Nobody can see it, and it is still there after the image has been
						recompressed, screenshotted, cropped, filtered, or run through one of the AI
						removers built for the visible text watermarks stamped on stock photos.
					</div>

					<div className="Technology-Stats" data-fade data-fade-delay="0.25" >
						<div className="Frame Technology-Stat" >
							<div className="Technology-Stat-Value" >{ PSNR_DB } dB</div>
							<div className="Technology-Stat-Label fs-16" >PSNR of the watermarked image, SSIM { SSIM_PERCENT }%: invisible to the eye</div>
						</div>
						<div className="Frame Technology-Stat" >
							<div className="Technology-Stat-Value" >{ EXACT_RECOVERY_AVERAGE }%</div>
							<div className="Technology-Stat-Label fs-16" >of identifiers recovered in full, averaged over 14 attacks</div>
						</div>
					</div>
				</div>
			</div>

			<div className="Pane-Light Page-Section Technology-Section" >
				<div className="Container" >
					<div className="Page-Heading" data-fade >How it works</div>

					<HowItWorks />

					<div className="Technology-Columns" >
						<div className="Technology-Column" data-fade >
							<div className="Technology-Column-Title" >Why neural</div>
							<div className="Page-Text fs-18" >
								A classical watermark is a hand-designed pattern, and every transformation
								its designer did not foresee breaks it. Neural watermarking trains the
								encoder and the decoder together, with the transformations an image meets
								online simulated between them, so robustness is learned rather than assumed.
							</div>
						</div>
						<div className="Technology-Column" data-fade data-fade-delay="0.1" >
							<div className="Technology-Column-Title" >Why it stays invisible</div>
							<div className="Page-Text fs-18" >
								The encoder adapts the mark to each image: stronger in textures where the eye
								cannot see it, absent on flat areas where it would show. The result measures
								{ ' ' }{ PSNR_DB } dB PSNR and { SSIM_PERCENT }% SSIM against the original, the second
								most invisible of the seven methods benchmarked.
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="Pane-Light Page-Section Technology-Section" >
				<div className="Container" >
					<div className="Page-Heading" data-fade >See for yourself.</div>
					<div className="Page-Text Technology-Slider-Text fs-18" data-fade data-fade-delay="0.1" >
						Drag the handle. The right half carries a watermark, the left half is the original.
					</div>
					<div className="Technology-Slider" data-fade data-fade-delay="0.15" >
						<CompareSlider />
					</div>
				</div>
			</div>

			{ /* What is left of the page is the report behind the figures above:
			     how the run was set up, then its results attack by attack and
			     method by method. */ }
			<div className="Pane-Light Page-Section Technology-Section" >
				<div className="Container" >
					<div className="Page-Label" >Annex</div>
					<div className="Title text-shadow-2" data-fade >Protocol and detailed results</div>

					<div className="Technology-Protocol" data-fade data-fade-delay="0.1" >
						<div className="Page-Heading" >Protocol</div>
						<div className="Page-Text fs-18" >
							Every figure on this page comes from the same run. { IMAGE_COUNT } images of
							COCO val2017, center-cropped and resized to 512×512. Each one is watermarked
							with its own { PAYLOAD_BITS }-bit identifier, then put through { ATTACK_COUNT } attacks,
							one setting each. What is measured is the share of those identifiers the decoder
							reads back exactly: all { PAYLOAD_BITS } bits correct, or the image counts as a
							failure, since a partial identifier identifies no one.
						</div>
					</div>
				</div>
			</div>

			<div className="Pane-Light Page-Section Technology-Section" >
				<div className="Container" >
					<div className="Page-Heading" data-fade >Built to survive what happens to an image online.</div>
					<div className="Page-Text Technology-Section-Intro fs-18" data-fade data-fade-delay="0.1" >
						Fourteen attacks reproduce the path of an image that leaks. For each one, the
						figure is the share of 1000 identifiers that come back without a single wrong
						bit.
					</div>

					<div className="Technology-Families" >
						{ ATTACK_FAMILIES.map( ( family: AttackFamily, index: number ): React.JSX.Element => (
							<FamilyCard family={ family } index={ index } key={ family.name } />
						) ) }
					</div>
				</div>
			</div>

			<div className="Pane-Light Page-Section Technology-Section" >
				<div className="Container" >
					<div className="Page-Heading" data-fade >Benchmarked against six reference methods.</div>
					<div className="Page-Text Technology-Section-Intro fs-18" data-fade data-fade-delay="0.1" >
						Same images, same { PAYLOAD_BITS }-bit payload, same fourteen attacks for every method.
						Averaged over the attacks, underlayer recovers the full identifier
						{ ' ' }{ EXACT_RECOVERY_AVERAGE }% of the time against { BEST_ALTERNATIVE_AVERAGE }% for the best
						alternative, and it is the only method above 90% on each of the fourteen. Against
						the AI removal chain, a remover built for visible text watermarks, it holds
						{ ' ' }{ AI_REMOVAL_EXACT }%, where the neural methods of the literature fall to
						{ ' ' }{ AI_REMOVAL_OTHERS }.
					</div>

					<div className="Technology-Methods" ref={ chartRef } style={ METHODS_STYLE } data-fade data-fade-delay="0.15" >
						<div className="Technology-Methods-Header fs-16" >
							<div>Method</div>
							<div>Identifiers recovered in full, averaged over 14 attacks</div>
							<div className="Technology-Methods-Header-Psnr" >PSNR</div>
						</div>
						{ METHODS.map( ( method: Method ): React.JSX.Element => (
							<MethodRow method={ method } key={ method.name } />
						) ) }
					</div>

					<div className="Technology-Method-Notes fs-16" data-fade data-fade-delay="0.2" >
						{ ROBUSTNESS_RUN.map( ( line: string ): React.JSX.Element => (
							<div key={ line } >{ line }</div>
						) ) }
					</div>
				</div>
			</div>

		</div>
		</Page>
	)
}
